import {
  Descendant,
  EditorVersion,
  PopulatedNode,
  ShortcutArgs,
  TemplateId,
  UpdateCallback,
} from '@hb/shared'
import { useCallback, useMemo } from 'react'
import { sendPracticeContracts } from '../../../backend/functions/practiceContracts'
import { usePractice } from '../../../hooks/backend/practice/usePractice'
import { usePopulatedUser } from '../../../hooks/backend/user/usePopulatedUser'
import { TemplateDataProvider } from '../contexts/data'
import { EditModeProvider } from '../contexts/editMode'
import { TemplateViewModal } from '../TemplateViewModal'

// The practice-contract analog of ProfileTemplateViewModal: compose a contract from a
// template (picked via the template menu), edit the populated rich text, and send it to the
// selected provider. Signer + practice data drive the template variables (Signer / Practice
// shortcut groups); the client-populated text is sent to the sendPracticeContracts callable.
export const PracticeContractComposeModal = ({
  practiceId,
  signerId,
  onClose,
}: {
  practiceId: string
  signerId: string
  onClose: () => void
}) => {
  const { user: signer } = usePopulatedUser(signerId)
  const { data: practice } = usePractice(practiceId)

  const shortcutArgs = useMemo<ShortcutArgs>(
    () => ({ assessmentId: null, user: signer, practice }),
    [signer, practice],
  )

  const handleSubmit = useCallback(
    async (
      text: PopulatedNode[],
      editorVersion: EditorVersion,
      templateId: TemplateId | null,
      name: string,
    ): Promise<UpdateCallback> => {
      if (!templateId) return { error: 'Internal error - missing template id' }
      try {
        await sendPracticeContracts({
          practiceId,
          toBeSignedByUser: signerId,
          templateId: templateId.templateId,
          name,
          text: text.length ? (text as unknown as Descendant[]) : null,
          editorVersion,
        })
        onClose()
        return { success: 'Practice contract sent' }
      } catch (err: any) {
        return { error: err?.message || 'Error sending practice contract' }
      }
    },
    [practiceId, signerId, onClose],
  )

  return (
    <EditModeProvider editMode="document">
      <TemplateDataProvider type="practiceContract" data={shortcutArgs}>
        <TemplateViewModal
          isOpen
          type="practiceContract"
          user={signer}
          submitText={{ submit: 'Send Practice Contract', confirm: 'Confirm send' }}
          onClose={onClose}
          onSubmit={handleSubmit}
        />
      </TemplateDataProvider>
    </EditModeProvider>
  )
}
